import { create } from './store';
import { createLogger } from 'shared/logger';
import {
  AppliedProfile,
  ConstraintDocument,
  ConstraintMode,
  ConstraintProfile,
} from 'types/ConstraintProfileTypes';
import { cloneDocument, diffDocumentPaths, documentsEqual, fillMissingGroups, normalizeRebalancingRiskForDiff, preservePortfolioFields, VarLimitsByPlan } from 'utils/constraintDocument';
import { ConstraintProfilesService } from 'shared/services/ConstraintProfilesService';

const log = createLogger('ConstraintsStore');

interface ConstraintsState {
  /** Active mode. Set from the JWT at chat start (wired in a later plan). */
  mode: ConstraintMode;
  /** The computed system-default Document for the current mode + plan. */
  predefiniti: ConstraintDocument | null;
  /** Plan reference data (VaR limits, composition tables) for panel display. */
  predefinitiReference: Record<string, unknown> | null;
  /**
   * Full predefiniti cache: every financial plan (1-8) per mode. Populated
   * once at session start by `fetchAllPredefinitiForMode` — one backend call
   * per mode, 8 plans each. All subsequent "switch to plan X" operations
   * (rebalancing-thread-import, panel slider) are pure cache lookups via
   * `setActivePredefiniti`. No further `/predefiniti` calls happen for the
   * lifetime of the session.
   */
  predefinitiByModeAndPlan: {
    builder: Record<number, ConstraintDocument | null>;
    rebalancing: Record<number, ConstraintDocument | null>;
  };
  /**
   * Plan-independent reference data per mode (value_at_risk_limits,
   * composition_macro, composition_macro_asset_allocation). Same across all
   * plans within a mode, so cached once per mode.
   */
  predefinitiReferenceByMode: { builder: Record<string, unknown> | null; rebalancing: Record<string, unknown> | null };
  /** The Document the panel renders and edits. */
  workingDocument: ConstraintDocument | null;
  /** The applied profile (id + name + baseline snapshot); null => predefiniti. */
  appliedProfile: AppliedProfile | null;
  /** `group.field` paths the agent changed and the advisor has not yet seen. */
  agentChangedPaths: string[];
  /**
   * Real Constraint Document groups whose section-level red dot the user
   * has already cleared (by opening the section's accordion). Independent
   * of `agentChangedPaths`: opening an accordion acknowledges the section
   * dot but the orange field borders inside survive until the user types
   * over each field. A fresh `metadata.updated` delta that touches a
   * group clears that group from this list so the section dot re-lights.
   */
  acknowledgedSectionGroups: string[];
  /** A Document an apply/reset produced that must be persisted to the agent; null when nothing pending. */
  pendingSync: ConstraintDocument | null;
  /**
   * True while a write to the server is in flight — silent sync via
   * `seedThread`, Invia via `continueThread`, or first-chat via
   * `createThreadAndStart`. Set to `true` immediately before the SDK call
   * and cleared in the `finally` of the awaiter, so it tracks the actual
   * WebSocket-stream lifetime (DONE event = promise resolution). Used by
   * `useThreadConstraintsState` to gate the panel into the `syncing` state
   * (Salva / Invia / Reset / dropdown disabled) so the user can't stack a
   * second write on top of the first.
   *
   * Replaces the prior content-matching `outgoingSyncSnapshot`. We no longer
   * compare incoming `thread.updated` echoes against a sent snapshot — the
   * SDK's `thread.updated` arrives as part of the same stream and the
   * promise resolution is the authoritative "server is done" signal. The
   * "is this echo our own write?" question for red-dot suppression is left
   * intentionally unanswered for now and will be addressed when the SDK
   * exposes `metadata.updated` as a subscribable event (only path the agent
   * uses to change constraints during a run).
   */
  syncInFlight: boolean;
}

interface ConstraintsActions {
  /**
   * Apply an agent-pushed delta. `newDoc` becomes the working document
   * outright (the agent's view wins; any uncommitted user edits are
   * overwritten, with the orange-border indicator drawing the user's
   * attention via `agentChangedPaths`). If `previousDoc` is provided, the
   * diff between the two is added to `agentChangedPaths` — that's the
   * per-field memory of "agent just touched this". When `previousDoc` is
   * null (first metadata frame in a fresh thread, or first delta the FE
   * has ever seen for this thread), the working document is adopted as
   * the baseline with NO change indicators raised.
   *
   * Replaces `setAgentDocument` for the inbound-metadata path: the SDK's
   * `useMetadataUpdate` hook now passes the wire-level delta + the
   * pre-merge snapshot, so the diff baseline is the agent's prior view,
   * not whatever the user happened to have in `workingDocument` at the
   * moment.
   */
  applyAgentDelta: (
    newDoc: ConstraintDocument,
    previousDoc: ConstraintDocument | null,
  ) => void;
  /**
   * Drop the given path from `agentChangedPaths`. Called when the user
   * types over the agent's value in that field — the orange border
   * clears for that single field while others stay lit. Idempotent if
   * the path isn't present.
   */
  acknowledgeFieldEdit: (path: string) => void;
  setMode: (mode: ConstraintMode) => void;
  /**
   * Populate the per-plan cache for a mode (all 8 plans + the mode's
   * shared reference data) from a single bulk-fetch response.
   */
  setPredefinitiBundleForMode: (
    mode: ConstraintMode,
    plansDict: Record<string, ConstraintDocument>,
    reference?: Record<string, unknown>,
  ) => void;
  /**
   * Switch the active predefiniti pointer to a specific (mode, plan) from
   * the cache, applying the SSO risk_profile overlay for rebalancing.
   * No backend call — the cache is prefetched at session start.
   */
  setActivePredefiniti: (
    mode: ConstraintMode,
    plan: number,
    ssoRiskProfile?: string | null,
  ) => void;
  /** Bulk-fetch all 8 plans + reference for a mode (one backend call). */
  fetchAllPredefinitiForMode: (mode: ConstraintMode) => Promise<void>;
  /** Clear all session-scoped state on logout. */
  clearSession: () => void;
  /** Apply a saved profile: working + baseline both become its snapshot. */
  applyProfile: (profile: ConstraintProfile) => void;
  /** Apply "Vincoli predefiniti": working becomes predefiniti, no applied profile. */
  applyPredefiniti: () => void;
  /** Apply one in-pane field edit. */
  applyEdit: (group: string, field: string, value: unknown) => void;
  /** Merge several field values into one group of the working Document at once. */
  applyGroupEdits: (group: string, fields: Record<string, unknown>) => void;
  /** Adopt a Document the agent produced; the applied profile is unchanged. */
  setAgentDocument: (doc: ConstraintDocument) => void;
  /** Clear the agent-changed paths belonging to the given real groups. */
  acknowledgeSection: (groups: string[]) => void;
  /** Revert the working Document to the applied-profile baseline (or predefiniti). */
  reset: () => void;
  /** Clear the pending-sync flag (called after the sync hook flushes it). */
  clearPendingSync: () => void;
  /** Set the in-flight sync flag (called from the writer hooks around their await). */
  setSyncInFlight: (value: boolean) => void;
  /** Re-anchor the baseline after a Salva: the working Document becomes the new baseline. */
  onProfileSaved: (profile: ConstraintProfile) => void;
  /**
   * Restore `appliedProfile` from a thread-metadata reference on page reload.
   * Unlike `applyProfile`, this does NOT touch `workingDocument` or
   * `pendingSync` — the working document has just been restored from
   * `METADATA_KEY` and re-syncing would be redundant. The snapshot in
   * `appliedProfile.document` is set to the current workingDocument so the
   * pane treats the restored state as unmodified.
   */
  restoreAppliedProfile: (profile: ConstraintProfile) => void;
  /**
   * Replace the applied-profile reference WITHOUT touching `workingDocument`,
   * and flag `pendingSync` so the new reference is persisted to thread
   * metadata via the silent-sync path. Used when a profile is deleted from
   * the catalog (here or in another session) and we need to re-baseline
   * threads that pointed at it — the thread's actual constraints stay, only
   * the baseline pointer moves to the new default (or null for predefiniti).
   * Pass `null` to clear the reference (baseline becomes predefiniti).
   */
  replaceAppliedProfile: (profile: ConstraintProfile | null) => void;
  /**
   * The baseline the working Document is diffed against.
   * The returned object reference is stable until `applyProfile`,
   * `onProfileSaved`, or `reset` replaces it, so it is safe to use directly
   * in a reactive Zustand selector.
   */
  baselineDocument: () => ConstraintDocument | null;
  /** True when the working Document diverges from the baseline ("(modificato)"). */
  isModified: () => boolean;
}

const emptyPlanCache = (): Record<number, ConstraintDocument | null> => ({
  1: null, 2: null, 3: null, 4: null, 5: null, 6: null, 7: null, 8: null,
});

const initialState: ConstraintsState = {
  mode: 'builder',
  predefiniti: null,
  predefinitiReference: null,
  predefinitiByModeAndPlan: {
    builder: emptyPlanCache(),
    rebalancing: emptyPlanCache(),
  },
  predefinitiReferenceByMode: { builder: null, rebalancing: null },
  workingDocument: null,
  appliedProfile: null,
  agentChangedPaths: [],
  acknowledgedSectionGroups: [],
  pendingSync: null,
  syncInFlight: false,
};

const constraintsStore = create<ConstraintsState & ConstraintsActions>()(
  (set, get) => ({
    ...initialState,

    setMode: (mode) => {
      // Switch the active mode using the mode's default plan (6 — the
      // balanced/dynamic centre). Callers that have a specific plan in
      // context (rebalancing-thread import, panel slider) use
      // `setActivePredefiniti(mode, plan, ssoRiskProfile?)` directly to
      // pin the right plan + SSO overlay in one call.
      get().setActivePredefiniti(mode, 6);
    },

    setPredefinitiBundleForMode: (mode, plansDict, reference) => {
      const { predefinitiByModeAndPlan, predefinitiReferenceByMode } = get();
      // Materialise the 1-8 plan cache from the backend dict. Keys arrive
      // as strings ("1".."8") in JSON; we normalise to numeric keys to
      // match `setActivePredefiniti`'s lookup.
      const nextPlanCache: Record<number, ConstraintDocument | null> = emptyPlanCache();
      for (const [planStr, doc] of Object.entries(plansDict)) {
        const plan = parseInt(planStr, 10);
        if (plan >= 1 && plan <= 8) {
          nextPlanCache[plan] = cloneDocument(doc);
        }
      }
      set({
        predefinitiByModeAndPlan: {
          ...predefinitiByModeAndPlan,
          [mode]: nextPlanCache,
        },
        predefinitiReferenceByMode: {
          ...predefinitiReferenceByMode,
          [mode]: reference ?? predefinitiReferenceByMode[mode],
        },
      });
    },

    setActivePredefiniti: (mode, plan, ssoRiskProfile) => {
      const { predefinitiByModeAndPlan, predefinitiReferenceByMode } = get();
      const cachedDoc = predefinitiByModeAndPlan[mode]?.[plan];
      const cachedRef = predefinitiReferenceByMode[mode];
      if (!cachedDoc) {
        // Cache miss: the session-start bulk fetch hasn't populated this
        // mode yet, or the plan is out of range. Defer — the active
        // pointer stays as-is. The caller is responsible for retrying
        // once the cache is loaded (gated by `constraintProfilesStore.loaded`).
        set({ mode });
        return;
      }
      const active = cloneDocument(cachedDoc);
      // For rebalancing, the SSO risk_profile from the thread's imported
      // portfolio is the live authoritative value (the backend ships
      // "dynamic" by default — see `build_predefiniti`). Overlay it onto
      // the active doc only; the per-plan cache entry stays untouched.
      if (
        mode === 'rebalancing' &&
        ssoRiskProfile &&
        active.risk &&
        typeof active.risk === 'object'
      ) {
        active.risk = { ...active.risk, risk_profile: ssoRiskProfile };
      }
      set({
        mode,
        predefiniti: active,
        predefinitiReference: cachedRef,
      });
    },

    fetchAllPredefinitiForMode: async (mode) => {
      try {
        const response = await ConstraintProfilesService.getPredefinitiAll(mode);
        get().setPredefinitiBundleForMode(
          mode,
          response.constraints,
          response.reference,
        );
      } catch (error) {
        log.error('fetch all predefiniti failed', { error });
      }
    },

    applyProfile: (profile) => {
      // two independent copies: one frozen as the baseline snapshot, one
      // editable as the working document
      // Groups the profile predates (saved under an older schema version, e.g.
      // `wrapper`) are filled from the predefiniti on BOTH copies, so the
      // accordion shows its defaults and the baseline diff stays clean.
      const snapshot = fillMissingGroups(
        cloneDocument(profile.constraints),
        get().predefiniti,
      );
      // `risk.risk_profile` is read-only from SSO (see the rebalancing
      // spec). The saved profile may carry a value from when the profile
      // was created — possibly a different SSO than the current session.
      // Pin the working document's value to `predefiniti.risk.risk_profile`,
      // which is the SSO-overlaid live source of truth. The baseline
      // snapshot keeps the profile's original value untouched, but the
      // diff excludes this path so it never trips `(modificato)`.
      let working = cloneDocument(snapshot);
      // Preserve the advisor's per-thread product subtree (and, in
      // rebalancing, the liquidity fields) from the current working
      // document. The profile snapshot doesn't carry these — they're
      // outside SAVABLE_PATHS — so without this overlay applying a
      // profile would silently wipe the advisor's preferred products,
      // selected lists, and (in rebalancing) the imported portfolio.
      working = preservePortfolioFields(get().workingDocument, working, get().mode);
      const ssoRiskProfile = get().predefiniti?.risk?.risk_profile;
      if (
        ssoRiskProfile &&
        working.risk &&
        typeof working.risk === 'object'
      ) {
        working.risk = { ...working.risk, risk_profile: ssoRiskProfile };
      }
      set({
        workingDocument: working,
        appliedProfile: {
          id: profile.id,
          name: profile.name,
          document: snapshot,
        },
        agentChangedPaths: [],
        acknowledgedSectionGroups: [],
        pendingSync: cloneDocument(working),
      });
    },

    applyPredefiniti: () => {
      const predefiniti = get().predefiniti;
      let working = predefiniti ? cloneDocument(predefiniti) : null;
      // See applyProfile note above — preserve the product subtree (and
      // liquidity in rebalancing) so switching to Predefiniti doesn't
      // silently wipe the advisor's per-thread picks.
      if (working) {
        working = preservePortfolioFields(get().workingDocument, working, get().mode);
      }
      set({
        workingDocument: working,
        appliedProfile: null,
        agentChangedPaths: [],
        pendingSync: working ? cloneDocument(working) : null,
      });
    },

    applyEdit: (group, field, value) => {
      const working = get().workingDocument;
      if (!working) {
        log.warn('applyEdit called with a null workingDocument');
        return;
      }
      const groupValue = { ...(working[group] ?? {}) };
      groupValue[field] = value;
      set({ workingDocument: { ...working, [group]: groupValue } });
    },

    applyGroupEdits: (group, fields) => {
      const working = get().workingDocument;
      if (!working) {
        log.warn('applyGroupEdits called with a null workingDocument');
        return;
      }
      set({
        workingDocument: {
          ...working,
          [group]: { ...(working[group] ?? {}), ...fields },
        },
      });
    },

    setAgentDocument: (doc) => {
      const previous = get().workingDocument;
      const changed = previous ? diffDocumentPaths(previous, doc, get().mode) : [];
      set({
        workingDocument: cloneDocument(doc),
        agentChangedPaths: [...get().agentChangedPaths, ...changed].filter(
          (path, i, all) => all.indexOf(path) === i,
        ),
      });
    },

    applyAgentDelta: (newDoc, previousDoc) => {
      const mode = get().mode;
      // First-frame guard: no previous metadata = baseline-only, no diff,
      // no indicators. Otherwise the very first thread.metadata arrival
      // would flag every populated field as an agent change.
      const changed = previousDoc
        ? diffDocumentPaths(previousDoc, newDoc, mode)
        : [];
      set({
        workingDocument: cloneDocument(newDoc),
        agentChangedPaths: [...get().agentChangedPaths, ...changed].filter(
          (path, i, all) => all.indexOf(path) === i,
        ),
      });
    },

    acknowledgeFieldEdit: (path) =>
      set({
        agentChangedPaths: get().agentChangedPaths.filter((p) => p !== path),
      }),

    acknowledgeSection: (groups) =>
      set({
        // Acknowledging a section clears its red dot but leaves the
        // per-field orange borders alive — the user has now seen WHICH
        // section the agent touched, but hasn't yet acknowledged the
        // individual values. The field-level paths in `agentChangedPaths`
        // stay until the user types over each one (or until a
        // whole-document reset via `clearAgentChangeIndicators`).
        acknowledgedSectionGroups: Array.from(
          new Set([...get().acknowledgedSectionGroups, ...groups]),
        ),
      }),

    reset: () => {
      const baseline = get().baselineDocument();
      let working = baseline ? cloneDocument(baseline) : null;
      // Reset snaps back to the profile/predefiniti baseline, which
      // doesn't carry the advisor's per-thread product subtree (nor, in
      // rebalancing, the imported-portfolio liquidity fields). Overlay
      // them from the current working document so Reset doesn't silently
      // wipe products / imported portfolio.
      if (working) {
        working = preservePortfolioFields(get().workingDocument, working, get().mode);
      }
      // Preserve the live SSO `risk.risk_profile` (read-only per the
      // rebalancing spec). The baseline may be an applied profile snapshot
      // that was frozen when the SSO had a different value; without this
      // overlay, Reset would clobber the live SSO value with the stale one.
      const ssoRiskProfile = get().predefiniti?.risk?.risk_profile;
      if (
        working &&
        ssoRiskProfile &&
        working.risk &&
        typeof working.risk === 'object'
      ) {
        working.risk = { ...working.risk, risk_profile: ssoRiskProfile };
      }
      set({
        workingDocument: working,
        // Reset is a snap back to the baseline; any prior agent-change
        // highlights are no longer meaningful. applyProfile/applyPredefiniti
        // clear this already — keep reset symmetric.
        agentChangedPaths: [],
        acknowledgedSectionGroups: [],
        pendingSync: working ? cloneDocument(working) : null,
      });
    },

    clearPendingSync: () => set({ pendingSync: null }),

    setSyncInFlight: (value) => set({ syncInFlight: value }),

    onProfileSaved: (profile) => {
      const working = get().workingDocument;
      if (!working) {
        log.warn('onProfileSaved called with a null workingDocument');
      }
      set({
        appliedProfile: {
          id: profile.id,
          name: profile.name,
          document: working ? cloneDocument(working) : {},
        },
        // Silent-sync the working document so useConstraintsSync fires and
        // writes the applied-profile reference (read from the store at sync
        // time) into thread metadata. Without this, a page refresh restores
        // workingDocument from METADATA_KEY but appliedProfile stays null —
        // the dropdown loses the "this profile is active" selection.
        pendingSync: working ? cloneDocument(working) : null,
      });
    },

    restoreAppliedProfile: (profile) => {
      // Use the profile's saved constraints (from the catalog) as the
      // baseline snapshot — same as applyProfile. This is what `isModified`
      // diffs `workingDocument` against, so the "modificato" badge correctly
      // surfaces when the thread's restored working document differs from
      // the profile's saved content (e.g. the user nudged a field after Salva
      // and asked the agent for a portfolio in the same session).
      // workingDocument is NOT touched — it was already restored from
      // METADATA_KEY by setAgentDocument and re-syncing would be redundant.
      set({
        appliedProfile: {
          id: profile.id,
          name: profile.name,
          document: cloneDocument(profile.constraints),
        },
      });
    },

    replaceAppliedProfile: (profile) => {
      const working = get().workingDocument;
      set({
        appliedProfile: profile
          ? {
              id: profile.id,
              name: profile.name,
              document: cloneDocument(profile.constraints),
            }
          : null,
        // Flag pendingSync with the (unchanged) workingDocument so
        // useConstraintsSync fires and writes the new reference (or empty
        // object for null) to thread metadata. Without this, the stale
        // metadata reference would resurface on the next page refresh.
        pendingSync: working ? cloneDocument(working) : null,
      });
    },

    baselineDocument: () => {
      const { appliedProfile, predefiniti } = get();
      return appliedProfile ? appliedProfile.document : predefiniti;
    },

    isModified: () => {
      const { workingDocument, mode } = get();
      const baseline = get().baselineDocument();
      if (!workingDocument || !baseline) {
        return false;
      }
      // In rebalancing, normalize the off-state expressions for
      // align_to_plan and target_var_min/max so that null and the
      // equivalent plan-default values are treated as equal. Without
      // this, a profile saved with explicit nulls (advisor cleared the
      // VaR fields) would flash `(modificato)` whenever the user toggles
      // the "Allinea al piano" checkbox off — the off-state writes
      // plan-default values while the baseline still carries nulls, even
      // though both represent the same Scenario-1 behaviour in the agent.
      if (mode === 'rebalancing') {
        const limits = (get().predefiniti?.risk as Record<string, unknown> | undefined)
          ?.risk_limits as VarLimitsByPlan | undefined;
        if (limits) {
          const a = normalizeRebalancingRiskForDiff(workingDocument, limits);
          const b = normalizeRebalancingRiskForDiff(baseline, limits);
          return !documentsEqual(a, b, mode);
        }
      }
      return !documentsEqual(workingDocument, baseline, mode);
    },

    clearSession: () =>
      set({
        ...initialState,
      }),
  }),
);

export default constraintsStore;
